'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { detectUserCountry, getAllCountries } from '@/lib/geolocation';
import {
  RiEyeLine,
  RiEyeOffLine,
  RiUserLine,
  RiMailLine,
  RiLockLine,
  RiLinksLine,
  RiGlobalLine,
  RiLoader4Line,
  RiPhoneLine,
} from 'react-icons/ri';

const COUNTRIES = getAllCountries().map(c => ({
  code: c.code,
  name: c.name,
  currency: c.currency,
  symbol: c.symbol,
  flag: c.flag,
  dailyMax: '0',
  note: 'Mobile Money',
  phoneCode: c.dialCode,
}));

type RegisterForm = {
  username: string;
  email: string;
  password: string;
  country: string;
  phone: string;
  referralCode: string;
};

type LoginForm = {
  email: string;
  password: string;
};

export default function AuthForm({ referralCode, initialMode }: { referralCode?: string; initialMode?: 'login' | 'register' }) {
  const router = useRouter();
  const [mode, setMode] = useState<'login' | 'register'>(initialMode || 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState(referralCode || '');
  const [initialReferralCode] = useState(referralCode || ''); // Track if user came from referral link
  
  // Auto-detect user's country
  const detectedCountry = detectUserCountry();
  const initialCountry = COUNTRIES.find(c => c.code === detectedCountry.code) || COUNTRIES[0];
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);

  const loginForm = useForm<LoginForm>({ mode: 'onBlur' });
  const registerForm = useForm<RegisterForm>({ 
    mode: 'onBlur',
    defaultValues: {
      country: initialCountry.code
    }
  });

  // Get redirect URL from search params
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Login failed');
      }

      toast.success('Welcome back to qashchatt!');
      
      // Check if user has paid registration fee
      if (!result.user?.hasPaidRegistration) {
        router.push('/registration-payment');
      } else {
        router.push(redirectUrl);
      }
    } catch (error) {
      loginForm.setError('email', {
        message: error instanceof Error ? error.message : 'Invalid credentials',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (data: RegisterForm) => {
    setLoading(true);
    try {
      // Combine country code with phone number
      const fullPhoneNumber = `${selectedCountry.phoneCode}${data.phone.replace(/\s/g, '')}`;
      
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          phone: fullPhoneNumber,
          referralCode: referralCodeInput,
          fromReferralLink: !!initialReferralCode // Track if user came from referral link
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Registration failed');
      }

      toast.success('Account created! Please complete your registration payment.');
      router.push('/registration-payment');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* Mobile logo */}
      <div className="flex lg:hidden items-center gap-2 mb-8">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="white" strokeWidth="2">
            <path d="M3 18 Q6 10 9 14 Q12 18 15 10 Q18 2 21 6" strokeLinecap="round" />
          </svg>
        </div>
        <span className="font-display font-700 text-xl text-foreground">qashchatt</span>
      </div>

      {/* Tab toggle */}
      <div className="flex bg-muted rounded-xl p-1 mb-8">
        <button
          onClick={() => setMode('login')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 ${
            mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Sign In
        </button>
        <button
          onClick={() => setMode('register')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 ${
            mode === 'register' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Create Account
        </button>
      </div>

      {mode === 'login' ? (
        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Email or Username</label>
            <div className="relative">
              <RiMailLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="username"
                {...loginForm.register('email', { required: 'Email or username is required' })}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            {loginForm.formState.errors.email && (
              <p className="text-red-400 text-xs mt-1">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
            <div className="relative">
              <RiLockLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                {...loginForm.register('password', { required: 'Password is required' })}
                className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <RiEyeOffLine size={16} /> : <RiEyeLine size={16} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-red-400 text-xs mt-1">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded border-border bg-input accent-primary" />
              <span className="text-sm text-muted-foreground">Remember me</span>
            </label>
            <button 
              type="button" 
              onClick={() => router.push('/forgot-password')}
              className="text-sm text-primary hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-600 text-sm transition-all duration-150 hover:brightness-110 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RiLoader4Line size={16} className="animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              'Sign In to qashchatt'
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
          {/* Country select */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Country</label>
            <p className="text-xs text-muted-foreground mb-2">Your country determines your earning currency</p>
            <div className="relative">
              <RiGlobalLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <select
                {...registerForm.register('country', { required: 'Select your country' })}
                onChange={(e) => {
                  const c = COUNTRIES.find((x) => x.code === e.target.value);
                  if (c) setSelectedCountry(c);
                  registerForm.setValue('country', e.target.value);
                }}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm focus:outline-none focus:border-primary transition-colors appearance-none"
              >
                {COUNTRIES.map((c) => (
                  <option key={`country-opt-${c.code}`} value={c.code}>
                    {c.flag} {c.name} ({c.currency})
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
              <span className="text-lg">{selectedCountry.flag}</span>
              <p className="text-xs text-primary font-600">
                You will earn in {selectedCountry.currency} — {selectedCountry.name} (Max: {selectedCountry.symbol}{selectedCountry.dailyMax}/day)
              </p>
            </div>
            {registerForm.formState.errors.country && (
              <p className="text-red-400 text-xs mt-1">{registerForm.formState.errors.country.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Username</label>
            <div className="relative">
              <RiUserLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="e.g. kofi_mensah"
                {...registerForm.register('username', {
                  required: 'Username is required',
                  minLength: { value: 4, message: 'At least 4 characters' },
                })}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            {registerForm.formState.errors.username && (
              <p className="text-red-400 text-xs mt-1">{registerForm.formState.errors.username.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Email Address</label>
            <div className="relative">
              <RiMailLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                placeholder="you@example.com"
                {...registerForm.register('email', { required: 'Email is required' })}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            {registerForm.formState.errors.email && (
              <p className="text-red-400 text-xs mt-1">{registerForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Phone Number</label>
            <div className="relative">
              <RiPhoneLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="tel"
                placeholder="700 000 000"
                {...registerForm.register('phone', { 
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9\s\-]{7,15}$/,
                    message: 'Invalid phone number format'
                  }
                })}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-600">
                {selectedCountry.phoneCode}
              </span>
            </div>
            {registerForm.formState.errors.phone && (
              <p className="text-red-400 text-xs mt-1">{registerForm.formState.errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
            <div className="relative">
              <RiLockLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Min. 8 characters"
                {...registerForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'At least 8 characters' },
                })}
                className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <RiEyeOffLine size={16} /> : <RiEyeLine size={16} />}
              </button>
            </div>
            {registerForm.formState.errors.password && (
              <p className="text-red-400 text-xs mt-1">{registerForm.formState.errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Referral Code {initialReferralCode ? <span className="text-muted-foreground font-400">(applied)</span> : <span className="text-muted-foreground font-400">(optional)</span>}
            </label>
            <div className="relative">
              <RiLinksLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="e.g. AmpaireEsther78"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value)}
                readOnly={!!initialReferralCode}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-input border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-600 text-sm transition-all duration-150 hover:brightness-110 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <>
                <RiLoader4Line size={16} className="animate-spin" />
                <span>Creating account...</span>
              </>
            ) : (
              'Create My Account'
            )}
          </button>

          <p className="text-xs text-muted-foreground text-center">
            By registering you agree to our{' '}
            <button type="button" className="text-primary hover:underline">Terms of Service</button>
            {' '}and{' '}
            <button type="button" className="text-primary hover:underline">Privacy Policy</button>
          </p>
        </form>
      )}
    </div>
  );
}